import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import SingleTextEditor from '../common/editors/SingleTextEditor';
import AIModelSelector from '../../pages/cerokm/components/AIModelSelector';
import AIResponseStats from './AIResponseStats';

export function useMounted() {
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return function useMountedEffectCleanup() {
      mountedRef.current = false;
    };
  }, []);
  return useCallback(() => mountedRef.current, [mountedRef]);
}

export default function AskAI({prompt = '', promptPrefix = '', placeholder, model: defaultModel = 'gpt-6.1-sol'}) {
  const {page} = useContext(LegoAdminPageContext);

  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastPrompt, setLastPrompt] = useState(prompt || '');
  const [activePromptId, setActivePromptId] = useState(null);
  const [model, setModel] = useState(defaultModel);
  const [responseStats, setResponseStats] = useState(null);
  const activePromptIdRef = useRef(null);
  const isMounted = useMounted();

  const isStreamingModel = (m) => m && m.startsWith('gemini');
  const streamingService = 'services/llm/prompt-streaming';
  const fallbackService = 'services/openai/prompt';

  useEffect(() => {
    if (prompt) askAI(prompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isStreamingModel(model)) return;
    let myId = activePromptIdRef.current;
    let partialOutput = '';

    const onProgress = ({ promptId, newOutput, stats }) => {
      if (myId !== promptId || !isMounted()) return;
      if (stats) {
        setResponseStats(stats);
        return;
      }
      partialOutput += newOutput;
      setText(partialOutput);
    };

    if (myId) {
      page.service(streamingService).on('prompt-progress', onProgress);
    }

    return () => {
      if (myId) {
        page.service(streamingService).removeListener('prompt-progress', onProgress);
      }
    };
  }, [activePromptId, model, isMounted, page]);

  const askAI = useCallback(async (rawPrompt) => {
    setLoading(true);
    setText('');
    setResponseStats(null);
    const fullPrompt = promptPrefix ? (promptPrefix + rawPrompt) : rawPrompt;
    try {
      if (isStreamingModel(model)) {
        let promptId = Math.ceil(Math.random() * 1000);
        setActivePromptId(promptId);
        activePromptIdRef.current = promptId;
        let res = await page.service(streamingService).create({ prompt: fullPrompt, promptId, model });
        if (promptId === activePromptIdRef.current && isMounted()) {
          setText(res);
          activePromptIdRef.current = null;
          setActivePromptId(null);
        }
      } else {
        setActivePromptId(null);
        activePromptIdRef.current = null;
        const res = await page.service(fallbackService).create({
          userPrompt: fullPrompt,
          options: { model },
        });
        if (isMounted()) {
          setText(res.message || (typeof res === 'string' ? res : ''));
        }
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [model, page, isMounted, promptPrefix]);

  // Rest of your component...

  return (
    <div>
      <textarea rows={6} className={'form-control form-control-small'} value={lastPrompt} onChange={e => setLastPrompt(e.target.value)} placeholder={placeholder}/>
        <div className='d-flex align-items-center'>
          <button className={'btn-primary btn mt-1'} onClick={() => askAI(lastPrompt)} disabled={loading || !lastPrompt.trim()}>
            {loading ? 'Processing...' : 'Ask AI'}
          </button>
          <span className='ml-2 w-25'>
            <AIModelSelector value={model} onChange={setModel} />
          </span>
          {loading && !text && <span className='ml-2 text-muted small mt-1'>
            <span className='spinner-border spinner-border-sm mr-1' role='status' />
            Waiting for response...
          </span>}
          
          <AIResponseStats stats={responseStats} />
        </div>

      <pre style={{whiteSpace: 'pre-wrap', minHeight: '30vh'}} className={'text-breakall broder rounde p-2 bg-light mt-2 small'}>
        {text?.split(/\*\*(.+?)\*\*/).map((part, i) => 
          i % 2 === 0 ? part : <strong key={i}>{part}</strong>
        )}
        {loading && text && <span className='text-muted'>
          <span className='spinner-border spinner-border-sm ml-1' role='status' />
        </span>}
      </pre>

    </div>
  );
}
